import { Check, Close } from "@mui/icons-material"
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import { useConnection } from "arweave-wallet-kit"
import React, { FormEvent, useCallback, useEffect } from "react"

import { deposit, PortfolioAgentInfo } from "@/api/portfolio-agents-api"
import { IdBlock } from "@/components/IdBlock"
import ShineBorder from "@/components/shine-border"
import useHopperPrice from "@/hooks/useHopper"
import { $hotAgentsMap, $hotOwnersMap } from "@/stores/hot-agents-store"
import { parseBigIntAsNumber, parseNumberAsBigInt } from "@/utils/format"

type DepositFormProps = {
  ticker: string
  activeAddress: string
  quoteTokenAddress: string
  agentId: string
  denomination: number
  refetch: () => void
  cannotTopup: boolean
  agentInfo?: PortfolioAgentInfo
}

export function DepositForm(props: DepositFormProps) {
  const {
    activeAddress,
    ticker,
    quoteTokenAddress,
    agentId,
    denomination,
    refetch,
    cannotTopup,
    agentInfo,
  } = props
  const { connect } = useConnection()

  const [open, setOpen] = React.useState(false)
  const [depositAmount, setDepositAmount] = React.useState("")
  const [errors, setErrors] = React.useState<Record<string, string>>({})
  const [backendError, setBackendError] = React.useState<Error | null>(null)
  const [loading, setLoading] = React.useState(false)
  const [receipts, setReceipts] = React.useState<{ amount: string; txId: string }[]>([])

  const { data: quoteTokenUsdPrice } = useHopperPrice(quoteTokenAddress, "usd")

  useEffect(() => {
    setReceipts([])
    setBackendError(null)
    setErrors({})
    setDepositAmount("")
    setLoading(false)
  }, [open])

  const handleSubmit = useCallback(
    (event: FormEvent) => {
      event.preventDefault()
      if (!agentInfo) return

      if (needsFirstDeposit) {
        setLoading(true)
        const depositAmountData = {
          quoteTokenId: quoteTokenAddress,
          amount: BigInt(agentInfo["Initial-Deposit"]),
          agentId,
          quoteTokenUsdPrice: quoteTokenUsdPrice as number,
        }

        deposit(depositAmountData)
          .then((txId) => {
            setReceipts((prev) => [
              ...prev,
              {
                amount: depositAmountData.amount.toString(),
                txId: txId,
              },
            ])
            refetch()
            $hotAgentsMap.setKey(agentId, new Date().getTime().toString())
            $hotOwnersMap.setKey(agentInfo["Portfolio-Owner"], new Date().getTime().toString())
            setDepositAmount("")
          })
          .catch(setBackendError)
          .finally(() => {
            setLoading(false)
          })

        return
      }

      const newErrors: Record<string, string> = {}

      if (Number(depositAmount) <= 0) newErrors.deposit = "Amount must be positive number"

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors)
        return
      }

      setErrors({})
      setLoading(true)

      const depositAmountData = {
        quoteTokenId: quoteTokenAddress,
        amount: parseNumberAsBigInt(depositAmount, denomination),
        agentId,
        quoteTokenUsdPrice: quoteTokenUsdPrice as number,
      }

      deposit(depositAmountData)
        .then((agentId) => {
          setReceipts((prev) => [
            ...prev,
            {
              amount: depositAmountData.amount.toString(),
              txId: agentId,
            },
          ])
          refetch()
          $hotAgentsMap.setKey(agentId, new Date().getTime().toString())
          $hotOwnersMap.setKey(agentInfo["Portfolio-Owner"], new Date().getTime().toString())
          setDepositAmount("")
        })
        .catch(setBackendError)
        .finally(() => {
          setLoading(false)
        })
    },
    [depositAmount, quoteTokenAddress, agentId, denomination, agentInfo],
  )

  const needsFirstDeposit = agentInfo?.Status === "AwaitingInitialDeposit"

  const topupButton = (
    <Button
      onClick={() => setOpen(true)}
      variant={needsFirstDeposit ? "text" : "outlined"}
      color="inherit"
      disabled={cannotTopup}
      fullWidth
      sx={{
        height: 40,
        width: 100,
        textTransform: "none",
        color: "rgb(81, 0, 255)",
      }}
    >
      <Typography variant="body2" component="span" color="inherit" fontWeight={700}>
        {needsFirstDeposit ? "Fund" : "Top-up"}
      </Typography>
    </Button>
  )

  return (
    <>
      <Box
        sx={{
          color: "rgba(90, 26, 226, 1)",
          "& > div": { width: "100% !important" },
          "&  button": { width: "100% !important" },
        }}
      >
        {needsFirstDeposit ? (
          <ShineBorder color="rgba(90, 26, 226, 1)" bgColor="var(--mui-palette-paperAlt)">
            {topupButton}
          </ShineBorder>
        ) : (
          topupButton
        )}
      </Box>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md">
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <DialogTitle>{needsFirstDeposit ? "Fund" : "Top-up"} Portfolio Agent</DialogTitle>

          <IconButton
            onClick={(event) => {
              setOpen(false)
            }}
          >
            <Close fontSize="medium" />
          </IconButton>
        </Stack>
        <DialogContent>
          <form onSubmit={handleSubmit}>
            <Stack gap={1}>
              <Stack direction={"row"} gap={1} maxWidth="100%" alignItems={"flex-start"}>
                <TextField
                  fullWidth
                  size="small"
                  margin="dense"
                  disabled={needsFirstDeposit}
                  label="Amount"
                  type="number"
                  value={
                    needsFirstDeposit
                      ? parseBigIntAsNumber(agentInfo["Initial-Deposit"], denomination)
                      : depositAmount
                  }
                  onChange={(e) => setDepositAmount(e.target.value)}
                  error={!!errors.deposit}
                  helperText={
                    needsFirstDeposit
                      ? "This is your configured initial deposit amount."
                      : errors.deposit
                  }
                  InputProps={{
                    endAdornment: <InputAdornment position="end">{ticker}</InputAdornment>,
                  }}
                />
                <Box height={44} paddingBottom={4}>
                  <Box sx={{ color: "rgba(90, 26, 226, 1)" }}>
                    <Button
                      type="submit"
                      variant="outlined"
                      color="inherit"
                      disabled={loading || cannotTopup}
                      sx={{ height: "40px", marginTop: 1, textTransform: "none" }}
                    >
                      <Typography variant="body2" component="span" color="inherit" fontWeight={700}>
                        {loading ? "Depositing..." : "Deposit"}
                      </Typography>
                    </Button>
                  </Box>
                </Box>
              </Stack>
              {receipts.length > 0 &&
                receipts.map((receipt) => (
                  <Stack gap={2} direction="row" alignItems="center">
                    <Check
                      color={"accent" as any}
                      sx={{
                        fontSize: 30,
                        padding: 1,
                        background: "rgba(var(--mui-palette-accent-mainChannel) / 0.1)",
                        borderRadius: 50,
                      }}
                    />
                    <span>
                      Deposited {parseBigIntAsNumber(receipt.amount, denomination)} {ticker}
                    </span>
                    •
                    <IdBlock
                      label={"View on ao.link"}
                      value={receipt.txId}
                      href={`https://ao.link/#/message/${receipt.txId}`}
                      hideCopyToClipboard
                    />
                    •
                    <IdBlock
                      label={"View on Dexi"}
                      value={receipt.txId}
                      href={`/entity/${receipt.txId}`}
                      hideCopyToClipboard
                    />
                  </Stack>
                ))}
              {backendError && <Alert severity="error">{String(backendError)}</Alert>}
            </Stack>
          </form>
        </DialogContent>
      </Dialog>
    </>
  )
}
